import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { EnvService } from '@core-mkt/services/env/env.service';
import { QueryParamService } from '@core-mkt/services/query-param/query-param.service';

/**
 * Regex to validate the api_pr query parameter value.
 * Expected format: pr-{number}-{hex} e.g. "pr-2098-38e3dd01f37e"
 */
const API_PR_PATTERN = /^pr-\d+-[a-f0-9]{1,40}$/;

/**
 * ApiPrOverrideService allows developers to override the XGRIT API base URL
 * by passing `?api_pr=pr-2098-38e3dd01f37e` in the URL.
 *
 * This is only active in non-production environments.
 * On the server side, the injected QueryParamService is replaced with
 * ServerQueryParamService which has access to the actual request query params.
 */
@Injectable({
  providedIn: 'root',
})
export class ApiPrOverrideService {
  private overrideUrl: string | null = null;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private queryParamService: QueryParamService,
    private envService: EnvService,
  ) {
    this.overrideUrl = this.resolveOverrideUrl();
  }

  /**
   * Returns the override XGRIT API URL if a valid api_pr param is present
   * and the environment is not production. Returns null otherwise.
   */
  getOverrideUrl(): string | null {
    return this.overrideUrl;
  }

  /**
   * Resolves the override URL from the api_pr query parameter.
   * Returns null if:
   * - Environment is production
   * - api_pr param is missing or empty
   * - api_pr param does not match the expected pattern
   */
  private resolveOverrideUrl(): string | null {
    const env = this.envService.get;

    // PROD safety: never allow override in production.
    // Note: env.production controls Angular optimizations and is true for dev/review builds too.
    // Use envMode to check the actual deployment target.
    if (env.envMode === 'prod') {
      return null;
    }

    const apiPrValue = this.getApiPrParam();

    if (!apiPrValue) {
      return null;
    }

    if (!API_PR_PATTERN.test(apiPrValue)) {
      return null;
    }

    // Build the Heroku PR review app URL with trailing slash
    return `https://ace-api-stg-${apiPrValue}.herokuapp.com/api/1/`;
  }

  /**
   * Reads the api_pr query parameter.
   * On CSR: reads directly from window.location.search (available immediately at bootstrap,
   * unlike ActivatedRoute which isn't populated yet when root services are constructed).
   * On SSR: uses ServerQueryParamService.getActualQueryParam to read from the Express request.
   */
  private getApiPrParam(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      const params = new URLSearchParams(window.location.search);
      return params.get('api_pr');
    }

    // SSR: ServerQueryParamService has getActualQueryParam for reading from the request.
    if ('getActualQueryParam' in this.queryParamService) {
      return (this.queryParamService as any).getActualQueryParam('api_pr');
    }

    return null;
  }
}
